import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

export function StoreProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ck_cart") || "[]"); } catch { return []; }
  });
  const [wishlist, setWishlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ck_wishlist") || "[]"); } catch { return []; }
  });

  useEffect(() => { localStorage.setItem("ck_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("ck_wishlist", JSON.stringify(wishlist)); }, [wishlist]);

  useEffect(() => {
    const token = localStorage.getItem("ck_token");
    if (!token) { setAuthReady(true); return; }
    api.get("/auth/me")
      .then((r) => { setUser(r.data); if (r.data.wishlist?.length) setWishlist(r.data.wishlist); })
      .catch(() => localStorage.removeItem("ck_token"))
      .finally(() => setAuthReady(true));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("ck_token", data.token);
    setUser(data.user);
    if (data.user.wishlist?.length) setWishlist(data.user.wishlist);
    return data.user;
  };
  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    localStorage.setItem("ck_token", data.token);
    setUser(data.user);
    return data.user;
  };
  const logout = () => { localStorage.removeItem("ck_token"); setUser(null); };

  const cartKey = (it) => `${it.type}:${it.id}:${it.colour || ""}:${JSON.stringify(it.selections || "")}`;

  const addToCart = useCallback((item) => {
    setCart((prev) => {
      const key = cartKey(item);
      const existing = prev.find((i) => cartKey(i) === key);
      if (existing) return prev.map((i) => (cartKey(i) === key ? { ...i, qty: i.qty + (item.qty || 1) } : i));
      return [...prev, { ...item, qty: item.qty || 1 }];
    });
    toast.success(`${item.title} added to your basket`);
  }, []);

  const updateQty = (key, qty) => setCart((prev) => prev.map((i) => (cartKey(i) === key ? { ...i, qty: Math.max(1, qty) } : i)));
  const removeFromCart = (key) => setCart((prev) => prev.filter((i) => cartKey(i) !== key));
  const clearCart = () => setCart([]);

  const toggleWishlist = async (productId) => {
    if (user) {
      try {
        const { data } = await api.post(`/wishlist/${productId}`);
        setWishlist(data);
        return;
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }
    setWishlist((prev) => (prev.includes(productId) ? prev.filter((p) => p !== productId) : [...prev, productId]));
  };

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const cartSubtotal = cart.reduce((s, i) => s + i.unit_price * i.qty, 0);

  return (
    <StoreContext.Provider value={{
      user, authReady, login, register, logout,
      cart, cartKey, addToCart, updateQty, removeFromCart, clearCart, cartCount, cartSubtotal,
      wishlist, toggleWishlist,
    }}>
      {children}
    </StoreContext.Provider>
  );
}
